package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initCrashTrackerKotlinNativePage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/CrashTrackerKotlinNative.md", FrontMatterElement.Builder { addScalar("title", "A crash tracker that fits on one box"); addScalar("searchTitle", "Self-hosted crash tracker in Kotlin/Native with Ktor and HTMX"); addScalar("date", "2025-12-06"); addScalar("description", "katcher — Kotlin/Native, Ktor and HTMX, in one binary small enough that nobody has to justify running it."); addScalar("library", "katcher"); }))
}

@Page("/blog/crash-tracker-kotlin-native")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun CrashTrackerKotlinNativePage() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Every crash reporting service I looked at wanted one of three things: my data, a JVM with a gigabyte of heap, or a Kubernetes cluster. For a side project — or an internal tool at a company that would rather not ship stack traces to a third party — none of those are a good trade.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/katcher") {
            org.jetbrains.compose.web.dom.Text("katcher")
        }
        org.jetbrains.compose.web.dom.Text(" starts from three constraints, and everything below follows from them.")
    }
    org.jetbrains.compose.web.dom.Ol {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("One binary.")
            }
            org.jetbrains.compose.web.dom.Text(" ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("scp")
            }
            org.jetbrains.compose.web.dom.Text(" it to a box, run it, done.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Small enough to ignore.")
            }
            org.jetbrains.compose.web.dom.Text(" If it uses less memory than a browser tab, nobody has to justify it in a planning meeting.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("No frontend build step.")
            }
            org.jetbrains.compose.web.dom.Text(" There is no bundler in this project and there will not be one.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Kotlin/Native covers the first two. HTMX covers the third.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("nothing-extra") }) {
        org.jetbrains.compose.web.dom.Text("Nothing extra")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The server is Ktor on the CIO engine, compiled to a native Linux executable. Storage is SQLite in the same process. The UI is server-rendered through Kotlin's HTML DSL, with HTMX for the parts that would otherwise need a framework.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Startup is measured in milliseconds and the resident set sits around 30–50 MB — which is what makes it reasonable as a sidecar or on the cheapest VPS you can rent.")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""[INFO] (io.ktor.server.Application): Application started in 0.01 seconds
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("For the database I went with ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("sqlx4k")
        }
        org.jetbrains.compose.web.dom.Text(" rather than SQLDelight: it is a Kotlin wrapper over the Rust ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("sqlx")
        }
        org.jetbrains.compose.web.dom.Text(" driver, reached through cinterop. You get Rust doing the byte handling and Kotlin doing the shape of the code.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("type-safe-html-and-htmx-for-the-rest") }) {
        org.jetbrains.compose.web.dom.Text("Type-safe HTML, and HTMX for the rest")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two things make server-rendered HTML pleasant rather than a chore.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The first is wrapping components instead of scattering class strings. A ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("button")
        }
        org.jetbrains.compose.web.dom.Text(" takes a variant and decides its own Tailwind classes; templates never spell them out. It is the shadcn idea, expressed as a DSL.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The second is Ktor's ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Resources")
        }
        org.jetbrains.compose.web.dom.Text(" plugin, so routes are a class hierarchy rather than strings. A small helper lets a deeply nested path be written on one line with the parent hierarchy hidden inside.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A paginated table then becomes ordinary code: rows issue an HTMX ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("GET")
        }
        org.jetbrains.compose.web.dom.Text(" with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("pushUrl = \"true\"")
        }
        org.jetbrains.compose.web.dom.Text(" so the back button keeps working, and the pagination controls target one element with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("HxSwap.innerHtml")
        }
        org.jetbrains.compose.web.dom.Text(" instead of reloading the page.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-image") }) {
        org.jetbrains.compose.web.dom.Text("The image")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two stages. The first builds the native binary:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-dockerfile") }) { org.jetbrains.compose.web.dom.Text("""FROM gradle:9.2.0-jdk21-jammy AS build
RUN gradle :server:linkReleaseExecutableNative --no-daemon --stacktrace
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The second copies the resulting ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("server.kexe")
        }
        org.jetbrains.compose.web.dom.Text(" into ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("distroless/cc-debian12")
        }
        org.jetbrains.compose.web.dom.Text(", along with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("libcrypt.so.1")
        }
        org.jetbrains.compose.web.dom.Text(" — which Kotlin/Native links against and distroless does not carry. No JDK, no Gradle, no development packages.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The two stages are a matched pair, and getting it wrong fails at ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("exec")
        }
        org.jetbrains.compose.web.dom.Text(" rather than at build time, with nothing but \"no such file or directory\" to explain it. The builder's glibc has to be no newer than the runtime's.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("authentication-it-does-not-implement") }) {
        org.jetbrains.compose.web.dom.Text("Authentication it does not implement")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("katcher has no login screen, and that is deliberate. It reads two headers:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""X-Auth-Request-User
X-Auth-Request-Email
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("If they are missing, it answers 401. The intended shape is")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""browser → Traefik/NGINX → oauth2-proxy (or any SSO) → katcher
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("which means you get Keycloak, Google or GitHub without a line of OAuth code in the project. In the Helm chart this shows up as two routes with different treatment: the UI behind the auth middleware, and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/api/reports")
        }
        org.jetbrains.compose.web.dom.Text(" deliberately outside it, because the services posting crashes hold no browser session and would be rejected before katcher ever saw them.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-client") }) {
        org.jetbrains.compose.web.dom.Text("The client")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Kotlin Multiplatform, currently JVM:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""Katcher.start {
    remoteHost = "https://katcher.example.com"
    appKey = "<YOUR_APP_KEY>"
    release = "1.0.0"
    environment = "Production"
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("It installs a global uncaught-exception handler and a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("CoroutineExceptionHandler")
        }
        org.jetbrains.compose.web.dom.Text(", and takes manual reports with a context map.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("One detail worth stealing: an atomic guard around the handler. If reporting a crash crashes, the second crash must not re-enter the reporter — only the first is processed while the flag is raised. Without it, a bad day becomes an infinite one.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/katcher") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/katcher")
        }
    }
}
